import AutoCompleter from "./auto_completer";

// This class implements a generic "drop-down-style" menu. Arrow-key
// navigation is suported, as is autocompletion.
//
// The menu HTML is not constructed here. It needs to be constructed in
// your view, and should look like this:
//
// ```html
// <script id="menu-hint-template" type="text/html">
//   <div class="menu-items">
//     <a href="#" class="list-group-item">bob@email.com</a>
//     <a href="#" class="list-group-item" data-term="jane@email.com">Jane Doe</a>
//   </div>
// </script>
// ```
//
// If a `data-term` attribute is present, it will be used in the search box.
// Otherwise, the element's inner text will be used.
//
// Important note: terms will be stripped of the following before use;
// * Leading & trailing whitespace
// * Non-space whitespace, like newlines or tabs
// * Duplicate whitespace. i.e. "  " (two spaces) will be converted to " " (one space)
//
// The whitespace limitations are important, because rogue whitespace can cause
// major problems with input highlighting.
//
export default class MenuHint {
  constructor($el, $container, token, params, query, input) {
    this.$el = $el;
    this.$container = $container;
    this.token = token;
    this.params = params;
    this.query = query;
    this.input = input;
    this.$container.html(this.params.template).show();
    this.$container.on("click.menuhint", ".list-group-item", (e) =>
      this.onClickItem(e),
    );
    this.autoCompleter = new AutoCompleter(this.autoCompleteTerms());
    this.$dropdownParent = this.$container.find(".menu-items");
    this.dropdownItems = this.$container.find(".list-group-item");
    this.selectOnEnter = false; // do you have to press enter to select item? Or does scrolling to it autoselect it?
  }

  destroy() {
    this.$container.off("click.menuhint", ".list-group-item");
  }

  autoCompleteTerms() {
    return Array.from(this.$container.find(".list-group-item")).map((item) => [
      this.term($(item)),
      item,
    ]);
  }

  setToken(token) {
    this.token = token;
    this.autoCompleter.complete({
      token: this.token,
      noMatch: () => {
        this.unfilterItems();
        return this.clearActive();
      },
      partialMatch: (replacementToken, match, matches) => {
        // Pressing keys like del doesn't change text but will update menus
        if (this.input.pressedKeyCode && this.input.pressedKeyCode > 45) {
          this.query.replaceToken(replacementToken, match.completionOffset);
        }
        this.filterItems(Array.from(matches).map((m) => m[1]));
        Array.from(matches).forEach((el) => $(el).show());
        this.clearActive();
        $(match.data).addClass("active");
        return this.scrollMiddle();
      },
    });
  }

  filterItems(matchItems) {
    this.$dropdownParent.empty();
    Array.from(this.dropdownItems)
      .filter((item) => Array.from(matchItems).includes(item))
      .forEach((item) => this.$dropdownParent.append(item));
  }

  unfilterItems() {
    this.$dropdownParent.empty();
    Array.from(this.dropdownItems).forEach((item) =>
      this.$dropdownParent.append(item),
    );
  }

  onClickItem(e) {
    e.preventDefault();
    const $target = $(e.currentTarget);
    this.makeActive($target);
    this.select(this.term($target));
  }

  term($el) {
    // Sloppy whitespace can mess up hint positioning, so clean it up
    return ($el.data("term") || $el.text()).replace(/\s+/g, " ").trim();
  }

  keyPress(keycode) {
    if (keycode === 38) {
      this.up();
    }
    if (keycode === 40) {
      this.down();
    }
    if (this.selectOnEnter && keycode === 13) {
      const activeValue = this.getActiveValue();
      if (activeValue) {
        this.select(activeValue);
        return false;
      }
    }
    return true;
  }

  up() {
    const $active = this.getActive();
    if ($active.length === 0 || $active.is(this.getFirstItem())) {
      this.makeActive(this.getLastItem());
    } else {
      const $prev = this.getItemBefore($active);
      if ($prev) {
        this.makeActive($prev);
      }
    }
    if (!this.selectOnEnter) {
      this.select(this.getActiveValue());
    }
    this.scrollEdge();
  }

  down() {
    const $active = this.getActive();
    if ($active.length === 0 || $active.is(this.getLastItem())) {
      this.makeActive(this.getFirstItem());
    } else {
      const $next = this.getItemAfter($active);
      if ($next) {
        this.makeActive($next);
      }
    }
    if (!this.selectOnEnter) {
      this.select(this.getActiveValue());
    }
    this.scrollEdge();
  }

  scrollMiddle() {
    const pos = this.getActive().position();
    if (pos) {
      const listItemHeight = 25;
      const containerHeight = this.$container.height();
      this.$container.scrollTop(
        pos.top - containerHeight / 2 - listItemHeight / 2,
      );
    }
  }

  scrollEdge() {
    const pos = this.getActive().position();
    if (pos) {
      const visibleTop = this.$container.scrollTop();
      const listItemHeight = 25;
      const containerHeight = this.$container.height();
      const visibleBottom = visibleTop + containerHeight - listItemHeight;
      // If the active link is cut-off by the top edge of the container
      if (pos.top < visibleTop) {
        this.$container.scrollTop(pos.top);
      }
      // If the active link is cut-off by the bottom edge of the container
      if (pos.top > visibleBottom) {
        this.$container.scrollTop(pos.top - containerHeight);
      }
    }
  }

  select(text) {
    this.query.replaceToken(this.token.cloneWithNewValue(text));
  }

  getActive() {
    return this.$container.find(".list-group-item.active");
  }

  getActiveValue() {
    return this.term(this.getActive());
  }

  clearActive(el) {
    $(el || this.getActive()).removeClass("active");
  }

  makeActive(el) {
    this.clearActive();
    $(el).addClass("active");
  }

  getItemBefore(el) {
    return $(el).prev(".list-group-item");
  }

  getItemAfter(el) {
    return $(el).next(".list-group-item");
  }

  getFirstItem() {
    return this.$container.find(".list-group-item:first-child");
  }

  getLastItem() {
    return this.$container.find(".list-group-item:last-child");
  }
}
